/**
 * Crashes: how hard a hit was, where, and what it does to the metal.
 *
 * A hit is measured as the change in the vehicle's velocity it causes in one
 * frame (`impactOf`), the way crash tests measure it. Under about 10 km/h it
 * is a knock, and nothing gives. Above, the vehicle crushes by how far
 * real ones do (`crushDepth`): a small car into a rigid barrier at 56 km/h
 * gives about 0.55–0.6 m at the front, roughly linear in speed above the
 * threshold. Doors and sills have far less room to give than a crumple zone,
 * a truck's cab sits on a stiff frame, and a motorbike's forks and plastics
 * fold over a few centimetres.
 *
 * The crush is a field over the vehicle (`crushField`), in its own frame (x
 * right, y up, nose toward -z, as the models are built):
 *
 * - **In**, along the push, fading out sideways from the contact over a radius
 *   that grows with the crush, and back into the vehicle over a crush zone at
 *   least twice the crush deep. That keeps it monotonic along the push: metal
 *   further in never passes metal nearer the contact, so a panel cannot turn
 *   inside out however often it is hit.
 * - **Folded**, across the crush zone: the metal buckles into pleats, out from
 *   the line of the push, a hand's width apart on a car, as a crumple zone
 *   folds. Out from the line, a bonnet tents up and a wing bows out. The
 *   folds stand out a share of their spacing, so they stay steep enough to
 *   see and never fold through each other.
 *
 * Small pieces (lamps, mirrors, plates, trim) break off when the metal under
 * them moves far enough (`breaksOff`); a mirror is also knocked off by
 * scraping along a wall. Lamps crack, glass crazes.
 */

/** Which side of the vehicle a hit is on. */
export type Zone = 'front' | 'rear' | 'left' | 'right';
/** How a vehicle is built, for how it crushes. */
export type Build = 'car' | 'truck' | 'moto';
export const ZONES: readonly Zone[] = ['front', 'rear', 'left', 'right'];

export type Vec3 = [number, number, number];

/** A hit, in the vehicle's own frame. */
export interface Impact {
  /** The change in velocity, m/s. */
  dv: number;
  /** The speed along the obstacle at the moment of the hit, m/s: for scrapes. */
  scrape: number;
  zone: Zone;
  /** Where it was hit, metres from the vehicle's middle on the ground. */
  point: Vec3;
  /** Unit direction the obstacle pushed the vehicle: into its body at `point`. */
  push: Vec3;
  /** How wide what it hit is, metres: a post a few tens of centimetres, a wall or a car wide (Infinity). */
  width: number;
}

/**
 * A hit as `PlayerController` sees it, in scene metres: the velocity before
 * and after (x east, z south), the unit normal the obstacle pushed along
 * (out of the obstacle), where on the body it touched relative to the
 * vehicle's middle, and the vehicle's heading. `height` is how high it struck,
 * and `width` how wide what it hit is.
 */
export function impactOf(
  before: { x: number; z: number }, after: { x: number; z: number }, normal: { x: number; z: number },
  contact: { x: number; z: number }, forward: { x: number; z: number }, height: number, width = Infinity,
): Impact {
  const dv = Math.hypot(after.x - before.x, after.z - before.z);
  // Along the obstacle's face: (nz, -nx).
  const scrape = Math.abs(before.x * normal.z - before.z * normal.x);
  // Into the vehicle's frame: right of forward (fx, fz) is (-fz, fx); the nose is -z.
  const toModel = (wx: number, wz: number): [number, number] => {
    const along = wx * forward.x + wz * forward.z;
    const across = wx * -forward.z + wz * forward.x;
    return [across, -along];
  };
  const [px, pz] = toModel(normal.x, normal.z);
  const [cx, cz] = toModel(contact.x, contact.z);
  const zone: Zone = Math.abs(pz) >= Math.abs(px) ? (pz > 0 ? 'front' : 'rear') : (px > 0 ? 'left' : 'right');
  const len = Math.hypot(px, pz) || 1;
  return { dv, scrape, zone, point: [cx, height, cz], push: [px / len, 0, pz / len], width };
}

/** How a vehicle gives at its ends and at its sides: metres of crush per km/h over the threshold, and the most it can take. */
interface Give {
  perKmh: number;
  max: number;
}

/** Below this change in velocity, km/h, a hit is a knock: nothing gives. */
export const KNOCK_KMH = 10;

const GIVE: Record<Build, { end: Give; side: Give }> = {
  // About 0.55 m at 56 km/h into a barrier, as a small car's crash test; a door has room for 0.4 m at most.
  car: { end: { perKmh: 0.012, max: 0.75 }, side: { perKmh: 0.009, max: 0.4 } },
  // A cab-over's face is on a stiff frame, with less in front of the driver than a car's bonnet.
  truck: { end: { perKmh: 0.008, max: 0.8 }, side: { perKmh: 0.006, max: 0.35 } },
  // Forks folding back and the front wheel into the frame: a quarter of a metre at 45 km/h.
  moto: { end: { perKmh: 0.007, max: 0.35 }, side: { perKmh: 0.005, max: 0.22 } },
};

/** How far a hit of `dv` m/s on `zone` crushes a vehicle of `build`, metres; 0 for a knock. */
export function crushDepth(dv: number, zone: Zone, build: Build): number {
  const kmh = dv * 3.6;
  if (kmh <= KNOCK_KMH) return 0;
  const give = zone === 'front' || zone === 'rear' ? GIVE[build].end : GIVE[build].side;
  return Math.min(give.max, give.perKmh * (kmh - KNOCK_KMH));
}

/** The most a zone of a vehicle of `build` can crush, metres: its 1.0 of damage. */
export function zoneLimit(zone: Zone, build: Build): number {
  return zone === 'front' || zone === 'rear' ? GIVE[build].end.max : GIVE[build].side.max;
}

/** One crush, as the field reads it: everything needed to apply it again to a fresh model. */
export interface Crush {
  point: Vec3;
  /** Unit, into the body. */
  push: Vec3;
  /** How far in, metres, at the contact. */
  depth: number;
  /**
   * How far out from the line of the push it is flat, metres, and where it
   * has faded to nothing: a wall flattens all it meets (both are `PLANAR`),
   * a car's end is about as wide as a car, a post's own width.
   */
  flat: number;
  radius: number;
  /** How deep into the body the crush zone runs, metres. */
  zone: number;
  /** Pleat spacing, metres, and where along it the first fold falls, radians. */
  fold: number;
  phase: number;
  /** For this hit's own dice: which parts break off. */
  seed: number;
}

/**
 * How far round a post a crush reaches past the post itself, metres: a
 * little, and more the deeper the post goes in, as the bonnet and the
 * bumper beam fold round it.
 */
const REACH: Record<Build, { base: number; per: number; max: number }> = {
  car: { base: 0.25, per: 0.6, max: 0.9 },
  truck: { base: 0.3, per: 0.6, max: 1.1 },
  moto: { base: 0.1, per: 0.5, max: 0.35 },
};
/**
 * A crush against a wall: flat over all the vehicle meets it with, however
 * wide and tall. Large rather than Infinity, so it keeps in storage as JSON.
 */
export const PLANAR = 100;
/** Wider than this, metres, what was hit is a wall: it flattens all it meets. */
const WALL = 4;
/** How far, metres, a crush against something car-wide fades out past its edge. */
const EDGE_FADE = 0.45;
/** How far behind its front a motorbike's forks take a blow through the wheel, metres. */
const WHEEL_THROUGH = 0.3;
/** Narrower than this, metres, what was hit is a post or a trunk: a pole crash. */
export const NARROW = 0.8;
/** A pole crash goes this much deeper than a wall at the same speed: the load is all on one place. */
const POLE = 1.4;
const FOLD: Record<Build, number> = { car: 0.17, truck: 0.24, moto: 0.07 };
/**
 * How far the pleats stand out, as a share of their spacing: a fold's flanks
 * slope at about 2π times this, 27°, which a faceted panel shows plainly. Much
 * more and a pleat leans over onto the next and the panel folds through
 * itself.
 */
const BUCKLE = 0.08;

/**
 * The crush a hit makes on a vehicle of `build`, or undefined for a knock.
 * Into a wall or a car the whole end or side goes in; round a post or a
 * trunk, a deep narrow V.
 */
export function crushOf(impact: Impact, build: Build, seed: number): Crush | undefined {
  const plain = crushDepth(impact.dv, impact.zone, build);
  if (plain <= 0) return undefined;
  const pole = impact.width < NARROW;
  const end = impact.zone === 'front' || impact.zone === 'rear';
  const depth = pole ? Math.min(zoneLimit(impact.zone, build), plain * POLE) : plain;
  const reach = REACH[build];
  // A wall flattens everything it meets; a car's end is a car wide, fading
  // past its edges; a post leaves a round dent, as wide as it reaches.
  const flat = pole ? Math.min(impact.width / 2, 0.3) : impact.width >= WALL ? PLANAR : impact.width / 2;
  const radius = pole ? Math.min(reach.max, reach.base + reach.per * depth) + flat
    : flat === PLANAR ? PLANAR : flat + EDGE_FADE + depth;
  // A motorbike's front wheel is left whole (it turns on its own), and
  // passes the blow straight back into the forks, the number board and the
  // headlamp behind it: the crush starts there.
  const through = build === 'moto' && end ? WHEEL_THROUGH : 0;
  return {
    point: [0, 1, 2].map(k => impact.point[k] + impact.push[k] * through) as Vec3,
    push: [...impact.push],
    depth,
    flat,
    radius,
    // Never shallower than twice the crush: see `crushField`. A motorbike's
    // whole front, from the forks to the steering head, gives.
    zone: Math.max(2.2 * depth, build === 'moto' ? 0.6 : 0.3),
    fold: FOLD[build],
    phase: (seed % 628) / 100,
    seed,
  };
}

/**
 * How far the crush `c` moves a point (x, y, z) of the body, into `out`.
 * Continuous everywhere, so a mesh moved by it never tears: two faces that
 * meet at a point move that point alike.
 */
export function crushField(c: Crush, x: number, y: number, z: number, out: Vec3): Vec3 {
  const rx = x - c.point[0], ry = y - c.point[1], rz = z - c.point[2];
  const [dx, dy, dz] = c.push;
  // How far in along the push, and how far out from its line.
  const s = rx * dx + ry * dy + rz * dz;
  const lx = rx - dx * s, ly = ry - dy * s, lz = rz - dz * s;
  const r = Math.hypot(lx, ly, lz);
  out[0] = out[1] = out[2] = 0;
  if (r >= c.radius || s >= c.zone) return out;
  // Flat across what was hit, fading out past its edge.
  const q = r <= c.flat ? 0 : (r - c.flat) / (c.radius - c.flat);
  const across = (1 - q * q) ** 2;
  // Everything at or ahead of the contact goes the whole way; in from it, less.
  const into = s <= 0 ? 1 : (1 - s / c.zone) ** 2;
  const u = c.depth * across * into;
  out[0] = dx * u;
  out[1] = dy * u;
  out[2] = dz * u;
  if (s > 0 && r > 1e-6) {
    // The pleats: out from the push's line, in folds along it, none at
    // either end of the crush zone, and fading in from the line itself.
    const window = Math.sin(Math.PI * (s / c.zone));
    const grown = Math.min(1, c.depth / 0.15);
    const b = BUCKLE * c.fold * grown * across * window * Math.sin((2 * Math.PI * s) / c.fold + c.phase) * Math.min(1, r / 0.15)
      // Against a wall the folds stand out from the middle of the face, no further than a body is wide.
      * (c.flat >= PLANAR ? Math.max(0, 1 - r / 2.5) : 1);
    out[0] += (lx / r) * b;
    out[1] += (ly / r) * b;
    out[2] += (lz / r) * b;
  }
  return out;
}

/** A small part's chance of breaking off when the metal under it moves `shift` metres. */
export function breakOffChance(shift: number, large: boolean): number {
  // A lamp or mirror goes at a few centimetres; a bumper must be well pushed in first.
  const from = large ? 0.16 : 0.04, span = large ? 0.24 : 0.1;
  return Math.max(0, Math.min(1, (shift - from) / span));
}

/** Whether a part is knocked off: by a dice roll in [0, 1) against `breakOffChance`. */
export function breaksOff(shift: number, large: boolean, roll: number): boolean {
  return roll < breakOffChance(shift, large);
}

/** Over this speed along a wall, m/s (about 20 km/h), a mirror on that side is scraped off. */
export const SCRAPE_OFF = 5.5;

/** A lamp under metal moved this far cracks and goes dark, metres. */
export const LAMP_BREAKS = 0.02;
/** Glass on metal moved this far crazes white, metres. */
export const GLASS_CRAZES = 0.03;

/**
 * How hard the camera is jolted by a hit of `dv` m/s: how far it is thrown,
 * metres, and over how long it settles, seconds.
 */
export function shakeFor(dv: number): { amplitude: number; seconds: number } {
  const amplitude = Math.max(0, Math.min(0.45, (dv - 1.5) * 0.03));
  return { amplitude, seconds: amplitude > 0 ? 0.25 + Math.min(0.6, dv * 0.03) : 0 };
}

/** Over this change in velocity head-on, m/s (about 25 km/h), a vehicle stops dead rather than grinding on. */
export const STOPS_DEAD = 7;

/** A seeded dice for one hit's choices: a fresh roll per part. */
export function dice(seed: number, part: number): number {
  let t = (seed * 2654435761 + part * 40503 + 0x6d2b79f5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** A vehicle's damage, kept with it: enough to build the same wreck again on a fresh model. */
export interface VehicleDamage {
  /** Every crush, in order, with where it was and how fast the vehicle slid along what it hit. */
  crushes: Array<{ crush: Crush; zone: Zone; scrape: number }>;
  /** How far gone each zone is, 0 to 1: its crush over the most it can take. */
  zones: Record<Zone, number>;
}

/** Most crushes kept: past this the oldest are merged into the zones' totals only. */
const KEPT = 64;

export function noDamage(): VehicleDamage {
  return { crushes: [], zones: { front: 0, rear: 0, left: 0, right: 0 } };
}

/** Add a crush to a vehicle's damage. */
export function addCrush(damage: VehicleDamage, crush: Crush, zone: Zone, scrape: number, build: Build): void {
  damage.crushes.push({ crush, zone, scrape });
  if (damage.crushes.length > KEPT) damage.crushes.shift();
  damage.zones[zone] = Math.min(1, damage.zones[zone] + crush.depth / zoneLimit(zone, build));
}

/** Whether something read from storage is damage this code can use. */
export function isDamage(value: unknown): value is VehicleDamage {
  if (typeof value !== 'object' || value === null) return false;
  const d = value as Partial<VehicleDamage>;
  return Array.isArray(d.crushes) && typeof d.zones === 'object' && d.zones !== null &&
    ZONES.every(z => typeof (d.zones as Record<string, unknown>)[z] === 'number') &&
    d.crushes.every(c => typeof c === 'object' && c !== null && Array.isArray(c.crush?.point) && Array.isArray(c.crush?.push) &&
      typeof c.crush.depth === 'number' && ZONES.includes(c.zone));
}
